import { Button, Typography } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { Offer } from '../../data/types';
import { dateTime, money } from '../../lib/format';
import { useTenantScope } from '../../store/selectors';
import DataTable, { type Column } from '../../ui/DataTable';
import { AboutThisPage, PageHeader, SectionCard, StatusChip } from '../../ui/primitives';
import { MONEY_TOTALS, offerTotals, type OfferTotals } from './offerTotals';

type Row = Offer & { totals: OfferTotals };

export default function OffersPage() {
  const scope = useTenantScope();
  const navigate = useNavigate();
  const rows: Row[] = scope.offers.map((offer) => ({
    ...offer,
    totals: offerTotals(offer, scope.applications, scope.classById.get(offer.classId)),
  }));
  const inSetup = scope.fundSetups.filter((setup) => setup.status !== 'PUBLISHED');
  const count = (key: keyof OfferTotals, label: string): Column<Row> => ({
    key,
    label,
    align: 'right',
    sortValue: (row) => row.totals[key],
    render: (row) => (MONEY_TOTALS.has(key) ? money(row.totals[key]) : row.totals[key]),
  });
  const columns: Column<Row>[] = [
    { key: 'status', label: 'Status', render: (row) => <StatusChip status={row.status} /> },
    {
      key: 'title',
      label: 'Offer',
      width: 280,
      sortValue: (row) => row.title,
      render: (row) => (
        <>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {row.title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Closes {dateTime(row.closesAt)}
          </Typography>
        </>
      ),
    },
    {
      key: 'class',
      label: 'Class',
      render: (row) =>
        `${scope.fundById.get(row.fundId)?.code ?? ''} · ${scope.classById.get(row.classId)?.name ?? ''}`,
    },
    count('applications', 'Applications'),
    count('inProgress', 'In progress'),
    count('amlReferred', 'AML/CTF referred'),
    count('notPaid', 'Not paid'),
    count('paid', 'Paid'),
    count('totalInitial', 'Total initial'),
    count('payableLater', 'Payable later'),
    count('total', 'Total'),
  ];
  return (
    <>
      <PageHeader
        title="Offers"
        subtitle="Every capital raise, with applied, paid and outstanding totals."
        actions={
          <Button variant="contained" onClick={() => navigate('/manager/setup')}>
            Set up a new deal
          </Button>
        }
      />
      <AboutThisPage>
        <p>
          &quot;Payable later&quot; is the unpaid balance on partly paid units, called after
          financial close. Applications move from in progress to paid as investors finish and their
          money is matched on the Payments page.
        </p>
      </AboutThisPage>
      {inSetup.length > 0 && (
        <SectionCard title="Deals in setup" sx={{ mb: 3 }}>
          {inSetup.map((setup) => (
            <Button
              key={setup.id}
              onClick={() => navigate(`/manager/setup/${setup.id}`)}
              sx={{ mr: 1 }}
            >
              {String(setup.values['fund.name'])} ·{' '}
              {setup.status === 'DRAFT' ? 'draft' : 'register active'}
            </Button>
          ))}
        </SectionCard>
      )}
      <SectionCard noPadding>
        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(row) => row.id}
          onRowClick={(row) => navigate(`/manager/offers/${row.id}`)}
          emptyMessage="No offers yet"
        />
      </SectionCard>
    </>
  );
}
